import { Pin, PinOff } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePinnedViews } from "@/hooks/use-pinned-views";

// Pin/unpin the screen you're on, from the app header — so the control is
// present on every screen, not only the inbox, which is what makes "pin any
// screen" true rather than aspirational. (The bottom tab bar it feeds is
// PinnedTabsBar.)
//
// It shows pinned state and toggles, rather than being a plain "add" action:
// without that, revisiting a folder and pinning again would stack duplicate
// tabs for the same screen.
export function PinCurrentViewButton() {
  const { isCurrentPinned, canPinCurrent, togglePinCurrent } = usePinnedViews();

  // A screen the label resolver doesn't recognise would pin as a nameless
  // tab — worse than not pinning it. Disabled and explained, not hidden:
  // a control that vanishes on some screens reads as a bug.
  const disabled = !isCurrentPinned && !canPinCurrent;

  return (
    <button
      type="button"
      onClick={togglePinCurrent}
      disabled={disabled}
      data-testid="button-pin-current-view"
      aria-pressed={isCurrentPinned}
      aria-label={isCurrentPinned ? "Unpin this view" : "Pin this view"}
      title={
        disabled
          ? "This screen can't be pinned"
          : isCurrentPinned
            ? "Unpin this view (Alt+P)"
            : "Pin this view to the tab bar (Alt+P)"
      }
      className={cn(
        "flex h-9 w-9 shrink-0 items-center justify-center rounded-md border transition-colors",
        isCurrentPinned
          ? "border-primary/50 bg-primary/10 text-primary hover:bg-primary/15"
          : "border-border text-muted-foreground hover:bg-secondary hover:text-foreground",
        disabled && "cursor-not-allowed opacity-40 hover:bg-transparent",
      )}
    >
      {isCurrentPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
    </button>
  );
}
